// Settings ▸ Analysis ▸ Reference Library — the pure model behind
// PlmReferenceLibraryPage and PlmReferenceTypesEditor. Kept out of the
// components so the rules that matter (the acceptable-range rule, what a slide's
// asbestos types must satisfy, how a control limit is calculated) are
// unit-testable without rendering anything.
//
// v7 (2026-10-08, prototype Reference-Library-v7, review 10/07):
//   • the LIST shows general information only; a slide's asbestos types — one
//     row per type, each with its own %, acceptable range, optics and RI limits —
//     open under the row;
//   • New / Edit open the full-page detail (General · Asbestos types ·
//     Certificate), saved as one unit;
//   • a slide is RETIRED, never deleted, because past QC reads point at it;
//   • control limits are CALCULATED from the lab's QC reads and uploaded as the
//     active set of a (scope, analyst) slice.
//
// The form began as a port of BTLIMS/EAS `05_Settings/ucReferenceLibrary.vb`
// (the PLM tab) — see
// docs/03-Code-Change-Documentation/Migration-Log/PLM-Wave2-ReferenceLibrary.md.
//
// THE API IS THE AUTHORITY for every rule here (PlmReferenceRangeRule,
// PlmReferenceTypeRules, PlmReferenceControlLimitRules). What follows mirrors
// them so the user is told before a round trip and sees the range while typing.

import { QC_CATEGORY_CUTS, qcFlags, type QcPoint } from './plmQcStats';

export const PLM_REF_LIB_BASE = "/api/v1/plm-reference-library";
export const PLM_REF_LIB_PAGE_ID = "set-plm-reference-library";
/** The audit form name. Unchanged by the v7 caption ("Reference Library"), so the
 *  history written before it stays under the same name. */
export const PLM_REF_LIB_FORM_NAME = "PLM Reference Library";

// ── control-limit scopes ───────────────────────────────────────────────────

/** The legacy "Control Limits with Analyst" radio group (`rgPLMMode`), stored as
 *  its 1-based ModeIndex so the numbers mean the same thing on both sides. */
export const LIMIT_SCOPES = [
  { value: 1, label: "Reference" },
  { value: 2, label: "Within Analyst" },
  { value: 3, label: "Within Laboratory" },
] as const;

export type LimitScope = (typeof LIMIT_SCOPES)[number]["value"];

export const SCOPE_WITHIN_LABORATORY: LimitScope = 3;

/** Within Laboratory pools every analyst, so it takes none. The other two scopes
 *  are per-analyst and show nothing until one is picked. */
export function scopeNeedsAnalyst(scope: LimitScope): boolean {
  return scope !== SCOPE_WITHIN_LABORATORY;
}

/** Whether a (scope, analyst) slice is fully chosen. */
export function canLoadLimits(scope: LimitScope, analystId: string): boolean {
  return !scopeNeedsAnalyst(scope) || analystId !== "";
}

export function scopeLabel(scope: number): string {
  return LIMIT_SCOPES.find((s) => s.value === scope)?.label ?? String(scope);
}

// ── vocabulary ─────────────────────────────────────────────────────────────
// Deliberately IDENTICAL to what PLM Result Entry records (PlmExamTab.tsx
// BIREFRINGENCE / MORPHOLOGY / ELONGATION / EXTINCTION), so a reference value and
// an analyst's observation are directly comparable — which is the whole purpose
// of a reference library. The API validates the same lists and the database
// enforces them as CHECK constraints, so all three agree by construction.

export const YES_NO = ["Yes", "No"] as const;
export const MORPHOLOGY_OPTIONS = [
  "Bundles", "Curved", "Ribbon", "Splayed", "Straight", "Wavy", "Uniform",
] as const;
export const EXTINCTION_OPTIONS = [
  "Oblique", "Parallel", "Undulose", "Incomplete", "None",
] as const;
export const ELONGATION_OPTIONS = ["Positive", "Negative", "Both"] as const;
export const BIREFRINGENCE_OPTIONS = ["Low", "Medium", "High", "Isotropic"] as const;
/** A fiber's optical character — BTLIMS's "FiberType" column. */
export const OPTICAL_CHARACTER_OPTIONS = ["Anisotropic", "Isotropic"] as const;

/** How a birefringence value READS on this form: the stored word plus the
 *  interference range it stands for (prototype v7). The stored value stays the
 *  bare word, so it still equals what PLM Result Entry records. */
export const BIREFRINGENCE_LABELS: Record<string, string> = {
  Low: "Low (<0.011)",
  Medium: "Medium (0.011–0.050)",
  High: "High (>0.050)",
  Isotropic: "Isotropic",
};
export const birefringenceLabel = (v: string) => BIREFRINGENCE_LABELS[v] ?? v;

// ── the acceptable-range rule ──────────────────────────────────────────────

/** ± 5 below 15 %, ± 20 from 15 % — percentage points, clamped to 0–100
 *  (Ky, ticket comment 73815). Mirrors PlmReferenceRangeRule. */
export const RANGE_RULE = { cut: 15, lowBand: 5, highBand: 20 } as const;

export const RANGE_RULE_HINT =
  `± ${RANGE_RULE.lowBand} below ${RANGE_RULE.cut}%, ± ${RANGE_RULE.highBand} from ${RANGE_RULE.cut}% (clamped 0–100)`;

export interface Range { lo: number; hi: number }

/** A typed value as a number, or null when it is blank or not a number. */
export function num(v: string | number | null | undefined): number | null {
  if (v == null) return null;
  const s = String(v).trim();
  if (s === "") return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
}

/** The rule's range for a reference value, or null while there is no value. */
export function ruleRange(quantity: string | number | null | undefined): Range | null {
  const q = num(quantity);
  if (q == null) return null;
  const band = q < RANGE_RULE.cut ? RANGE_RULE.lowBand : RANGE_RULE.highBand;
  return {
    lo: Math.max(0, Number((q - band).toFixed(2))),
    hi: Math.min(100, Number((q + band).toFixed(2))),
  };
}

// ── asbestos types ─────────────────────────────────────────────────────────

export type RangeMode = "Rule" | "Override";

/** One asbestos type of a slide, as the detail edits it: every value is the text
 *  in its box, so a half-typed number is never coerced. */
export interface PlmReferenceTypeRow {
  /** Stable React key — client-side only. */
  key: string;
  asbestosType: string;
  quantity: string;
  rangeMode: RangeMode;
  overrideLow: string;
  overrideHigh: string;
  overrideSource: string;
  opticalCharacter: string;
  birefringence: string;
  pleochroism: string;
  morphology: string;
  extinction: string;
  signOfElongation: string;
  meanRiAlpha: string;
  meanRiGamma: string;
  riAlphaLow: string;
  riAlphaHigh: string;
  riGammaLow: string;
  riGammaHigh: string;
}

let typeKeySeq = 0;
const nextTypeKey = () => `t${++typeKeySeq}`;

/** A number from the API as the text a box shows: no trailing zeros. */
function numText(v: unknown): string {
  if (v == null || v === "") return "";
  const n = Number(v);
  return Number.isFinite(n) ? String(n) : "";
}

/** Server payload → editable row. */
export function toTypeRow(r: Record<string, unknown>): PlmReferenceTypeRow {
  const s = (k: string) => (r[k] == null ? "" : String(r[k]));
  return {
    key: nextTypeKey(),
    asbestosType: s("asbestosType"),
    quantity: numText(r.quantity),
    rangeMode: s("rangeMode").toLowerCase() === "override" ? "Override" : "Rule",
    overrideLow: numText(r.overrideLow),
    overrideHigh: numText(r.overrideHigh),
    overrideSource: s("overrideSource"),
    opticalCharacter: s("opticalCharacter"),
    birefringence: s("birefringence"),
    pleochroism: s("pleochroism"),
    morphology: s("morphology"),
    extinction: s("extinction"),
    signOfElongation: s("signOfElongation"),
    meanRiAlpha: numText(r.meanRiAlpha),
    meanRiGamma: numText(r.meanRiGamma),
    riAlphaLow: numText(r.riAlphaLow),
    riAlphaHigh: numText(r.riAlphaHigh),
    riGammaLow: numText(r.riGammaLow),
    riGammaHigh: numText(r.riGammaHigh),
  };
}

/** A new, empty type row. Nothing is pre-filled except the name: an empty box
 *  must look empty, never like a default (prototype v3 note). */
export function blankType(asbestosType: string): PlmReferenceTypeRow {
  return { ...toTypeRow({}), asbestosType };
}

/** The first offered type the slide does not hold yet (else the first one) —
 *  what "+ Add asbestos type" starts a new row with. */
export function nextTypeName(rows: readonly PlmReferenceTypeRow[], options: readonly string[]): string {
  const used = new Set(rows.map((r) => r.asbestosType.trim().toLowerCase()));
  return options.find((o) => !used.has(o.toLowerCase())) ?? options[0] ?? "";
}

/** The range a read of this type is judged against: the certified override when
 *  the row carries a complete one, otherwise the rule. Null = nothing to show. */
export function effectiveRange(t: PlmReferenceTypeRow): Range | null {
  if (t.rangeMode === "Override") {
    const lo = num(t.overrideLow), hi = num(t.overrideHigh);
    return lo != null && hi != null ? { lo, hi } : null;
  }
  return ruleRange(t.quantity);
}

export const rangeText = (r: Range | null): string => (r ? `${r.lo}–${r.hi}%` : "—");

/** Switching a row to Override starts from the rule's range — the user then
 *  types the certified one over it. Values already entered are kept. */
export function withRangeMode(t: PlmReferenceTypeRow, mode: RangeMode): PlmReferenceTypeRow {
  if (mode === "Override" && t.rangeMode !== "Override") {
    const r = ruleRange(t.quantity);
    return {
      ...t,
      rangeMode: mode,
      overrideLow: t.overrideLow || (r ? String(r.lo) : ""),
      overrideHigh: t.overrideHigh || (r ? String(r.hi) : ""),
    };
  }
  return { ...t, rangeMode: mode };
}

/** Low > high in a low–high pair (both entered). */
export const pairInverted = (low: string, high: string): boolean => {
  const lo = num(low), hi = num(high);
  return lo != null && hi != null && lo > hi;
};

/** Every problem with a slide's types, in the order a user would fix them —
 *  the wording of PlmReferenceTypeRules.Validate, which re-checks all of it. */
export function validateTypes(rows: readonly PlmReferenceTypeRow[]): string[] {
  const errors: string[] = [];
  const seen = new Set<string>();
  let total = 0;
  for (const t of rows) {
    const name = t.asbestosType.trim();
    if (!name) { errors.push("Asbestos type must not be empty."); continue; }
    const key = name.toLowerCase();
    if (seen.has(key)) errors.push(`${name} is listed twice.`);
    seen.add(key);

    const q = num(t.quantity);
    const qOk = q != null && q > 0 && q <= 100;
    if (!qOk) errors.push(`${name}: % must be > 0 and ≤ 100.`);
    else total += q;

    if (t.rangeMode === "Override") {
      const lo = num(t.overrideLow), hi = num(t.overrideHigh);
      if (lo == null || hi == null) errors.push(`${name}: the override range needs low and high.`);
      else if (lo < 0 || hi > 100) errors.push(`${name}: the override range must be between 0 and 100%.`);
      else if (qOk && !(lo <= q && q <= hi)) errors.push(`${name}: the override range ${lo}–${hi}% must contain ${q}%.`);
    }

    const pairs: Array<[string, string, string]> = [
      ["α", t.riAlphaLow, t.riAlphaHigh], ["γ", t.riGammaLow, t.riGammaHigh],
    ];
    for (const [axis, low, high] of pairs) {
      if (isBadNumber(low) || isBadNumber(high)) errors.push(`${name}: RI ${axis} must be a number.`);
      else if (pairInverted(low, high)) errors.push(`${name}: RI ${axis} low must be ≤ high.`);
    }
    if (isBadNumber(t.meanRiAlpha) || isBadNumber(t.meanRiGamma)) errors.push(`${name}: the mean RI must be a number.`);
  }
  // Float sums pick up dust (70.1 + 29.9); compare what the user typed.
  if (Number(total.toFixed(4)) > 100)
    errors.push(`The asbestos total is ${Number(total.toFixed(4))}% — it cannot exceed 100%.`);
  return errors;
}

/** Something typed that is not a number (blank is fine — the box is optional). */
const isBadNumber = (v: string): boolean => v.trim() !== "" && num(v) == null;

/** Editable row → API payload. A blank box is null, never "" or 0. */
export function toTypePayload(t: PlmReferenceTypeRow): Record<string, unknown> {
  const ovr = t.rangeMode === "Override";
  return {
    asbestosType: nul(t.asbestosType),
    quantity: num(t.quantity),
    rangeMode: t.rangeMode,
    overrideLow: ovr ? num(t.overrideLow) : null,
    overrideHigh: ovr ? num(t.overrideHigh) : null,
    overrideSource: ovr ? nul(t.overrideSource) : null,
    opticalCharacter: nul(t.opticalCharacter),
    birefringence: nul(t.birefringence),
    pleochroism: nul(t.pleochroism),
    morphology: nul(t.morphology),
    extinction: nul(t.extinction),
    signOfElongation: nul(t.signOfElongation),
    meanRiAlpha: num(t.meanRiAlpha),
    meanRiGamma: num(t.meanRiGamma),
    riAlphaLow: num(t.riAlphaLow),
    riAlphaHigh: num(t.riAlphaHigh),
    riGammaLow: num(t.riGammaLow),
    riGammaHigh: num(t.riGammaHigh),
  };
}

/** "α 1.55 · γ 1.559", or "" when neither is set. */
export function meanRiText(t: PlmReferenceTypeRow): string {
  return t.meanRiAlpha || t.meanRiGamma ? `α ${t.meanRiAlpha || "—"} · γ ${t.meanRiGamma || "—"}` : "";
}

/** "α 1.543–1.557 · γ 1.552–1.566", or "" when no limit is set. */
export function riLimitsText(t: PlmReferenceTypeRow): string {
  const any = t.riAlphaLow || t.riAlphaHigh || t.riGammaLow || t.riGammaHigh;
  if (!any) return "";
  return `α ${t.riAlphaLow || "—"}–${t.riAlphaHigh || "—"} · γ ${t.riGammaLow || "—"}–${t.riGammaHigh || "—"}`;
}

// ── slides (the list) ──────────────────────────────────────────────────────

export type SlideStatus = "Active" | "Retired" | "Incomplete";

/** One reference slide as the list shows it: general information, plus its
 *  asbestos types for the panel that opens under the row. */
export interface PlmReferenceSlideRow {
  id: string;
  referenceId: string;
  source: string;
  originalSourceId: string;
  matrix: string;
  material: string;
  storage: string;
  typeCount: number;
  /** The types as one line of text ("Amosite 12% · Chrysotile 2%", or "ND") —
   *  what Export and the search read. */
  typesText: string;
  status: SlideStatus;
  attachmentOid: string;
  attachmentName: string;
  types: PlmReferenceTypeRow[];
}

export function toSlideRow(r: Record<string, unknown>): PlmReferenceSlideRow {
  const s = (k: string) => (r[k] == null ? "" : String(r[k]));
  const types = (Array.isArray(r.types) ? r.types : []).map((t) => toTypeRow(t as Record<string, unknown>));
  const status = s("status");
  return {
    id: s("oid"),
    referenceId: s("referenceId"),
    source: s("source"),
    originalSourceId: s("originalSourceId"),
    matrix: s("matrix"),
    material: s("material"),
    storage: s("storage"),
    typeCount: types.length,
    typesText: types.length ? types.map((t) => `${t.asbestosType} ${t.quantity}%`).join(" · ") : "ND",
    status: status === "Retired" || status === "Incomplete" ? status : "Active",
    attachmentOid: s("attachmentOid"),
    attachmentName: s("attachmentName"),
    types,
  };
}

/** The status chips above the list. */
export const STATUS_FILTERS = ["Active", "Retired", "All"] as const;
export type StatusFilter = (typeof STATUS_FILTERS)[number];

/** "Retired" is everything that cannot be picked for a reference read — retired
 *  slides AND incomplete ones (prototype `!r.active`); the Status column tells
 *  the two apart. */
export function matchesStatus(row: Pick<PlmReferenceSlideRow, "status">, filter: StatusFilter): boolean {
  if (filter === "All") return true;
  return filter === "Active" ? row.status === "Active" : row.status !== "Active";
}

export function statusCounts(rows: readonly Pick<PlmReferenceSlideRow, "status">[]): Record<StatusFilter, number> {
  const active = rows.filter((r) => r.status === "Active").length;
  return { Active: active, Retired: rows.length - active, All: rows.length };
}

/** The search box: ID, source, original ID, matrix, material, storage, status
 *  and the asbestos types. */
export function matchesSearch(row: PlmReferenceSlideRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  return [
    row.referenceId, row.source, row.originalSourceId, row.matrix, row.material,
    row.storage, row.status, row.typesText,
  ].some((v) => v.toLowerCase().includes(q));
}

export interface LookupOption { id: string; name: string }

export interface PlmReferenceLookups {
  matrices: LookupOption[];
  materials: LookupOption[];
  colors: LookupOption[];
  fiberTypes: LookupOption[];
  analysts: LookupOption[];
  /** Text lists (id = name = the stored value). */
  sources: LookupOption[];
  asbestosTypes: LookupOption[];
  storages: LookupOption[];
}

export const EMPTY_LOOKUPS: PlmReferenceLookups = {
  matrices: [], materials: [], colors: [], fiberTypes: [], analysts: [],
  sources: [], asbestosTypes: [], storages: [],
};

// ── control limits: calculate → review → upload ────────────────────────────
//
// The values come from the SAME points the PLM QC charts plot (plmQcStats
// `toPoints` / `dailyQcPoints`), so a limit here and a chart there are computed
// from one definition of a read and of an error:
//   Reference          one analyst's reference reads   value = read − reference
//   Within Analyst     one analyst's own duplicates    value = r1 − r2
//   Within Laboratory  every duplicate, pooled         value = r1 − r2
// A pair where both reads found nothing (0 / 0) is left out, as the charts leave
// it out of their limits: it always checks at zero and would flatter the spread.

export type LimitBasis = "sd" | "iqr";

export const LIMIT_BASES: ReadonlyArray<{ value: LimitBasis; label: string }> = [
  { value: "sd", label: "SD — mean ± 3σ" },
  { value: "iqr", label: "IQR — Q1 − 1.5·IQR … Q3 + 1.5·IQR" },
];

/** One value that feeds a limit, with the concentration its category is judged on. */
export interface LimitPoint {
  /** The reference value (a reference read) or the mean of the two reads. */
  m: number;
  /** read − reference, or r1 − r2. */
  y: number;
  date: string | null;
}

/** The points of one (scope, analyst) slice. `std` are the reference reads,
 *  `pairs` the duplicate re-reads. */
export function limitSeries(
  scope: LimitScope, analystName: string,
  std: readonly QcPoint[], pairs: readonly QcPoint[],
): LimitPoint[] {
  if (scope === 1) {
    return std
      .filter((p) => !p.ndPair && p.analyst === analystName)
      .map((p) => ({ m: p.original, y: p.error, date: p.date }));
  }
  const dup = (p: QcPoint): LimitPoint => {
    const f = qcFlags(p);
    return { m: f.mean, y: f.d, date: p.date };
  };
  if (scope === 2) {
    // The analyst's OWN repeatability: they read both the original and the re-read.
    return pairs
      .filter((p) => !p.ndPair && p.kind === "Intra" && p.analystOriginal === analystName)
      .map(dup);
  }
  return pairs.filter((p) => !p.ndPair).map(dup);
}

/** How many of the most recent months (that have data) a calculation looks at —
 *  the charts' own "Rolling 3 months". */
export const LIMIT_WINDOW_MONTHS = 3;

/** The points of the last `months` calendar months that have any. A point with
 *  no date cannot be placed in a month and is left out. */
export function recentMonths<T extends { date: string | null }>(points: readonly T[], months = LIMIT_WINDOW_MONTHS): T[] {
  const key = (d: string | null) => (d && /^\d{4}-\d{2}/.test(d) ? d.slice(0, 7) : "");
  const present = [...new Set(points.map((p) => key(p.date)).filter(Boolean))].sort();
  const keep = new Set(present.slice(-months));
  return points.filter((p) => keep.has(key(p.date)));
}

export type LimitCategory = 1 | 2 | 3;
export const LIMIT_CATEGORIES: readonly LimitCategory[] = [1, 2, 3];

/** Category 1 / 2 / 3 on the charts' cut-offs: ≤ 1 % · 1 – <15 % · ≥ 15 %. */
export function categoryOf(m: number): LimitCategory {
  const [c1, c2] = QC_CATEGORY_CUTS;
  return m <= c1 ? 1 : m < c2 ? 2 : 3;
}

export function categoryLabel(c: LimitCategory): string {
  const [c1, c2] = QC_CATEGORY_CUTS;
  return c === 1 ? `≤ ${c1}%` : c === 2 ? `${c1} – <${c2}%` : `≥ ${c2}%`;
}

/** One calculated row. Every statistic is null when the category has no counts. */
export interface CalculatedLimit {
  category: LimitCategory;
  n: number;
  mean: number | null;
  sd: number | null;
  iqr: number | null;
  lcl: number | null;
  ucl: number | null;
}

/** Linear-interpolated quantile (the prototype's `quant`). */
export function quantile(values: readonly number[], q: number): number {
  const s = [...values].sort((a, b) => a - b);
  const pos = (s.length - 1) * q;
  const base = Math.floor(pos);
  const next = s[base + 1];
  return s[base]! + (next !== undefined ? (pos - base) * (next - s[base]!) : 0);
}

/**
 * Mean, POPULATION σ (÷ n — the lab's workbook and the charts), IQR and the
 * limits of each concentration category:
 *   SD  basis   mean ± 3σ
 *   IQR basis   Q1 − 1.5·IQR … Q3 + 1.5·IQR
 */
export function calculateLimits(points: readonly LimitPoint[], basis: LimitBasis): CalculatedLimit[] {
  return LIMIT_CATEGORIES.map((category) => {
    const ys = points.filter((p) => categoryOf(p.m) === category).map((p) => p.y);
    const n = ys.length;
    if (!n) return { category, n: 0, mean: null, sd: null, iqr: null, lcl: null, ucl: null };
    const mean = ys.reduce((a, b) => a + b, 0) / n;
    const sd = Math.sqrt(ys.reduce((a, b) => a + (b - mean) ** 2, 0) / n);
    const q1 = quantile(ys, 0.25), q3 = quantile(ys, 0.75), iqr = q3 - q1;
    const [lcl, ucl] = basis === "sd" ? [mean - 3 * sd, mean + 3 * sd] : [q1 - 1.5 * iqr, q3 + 1.5 * iqr];
    return { category, n, mean, sd, iqr, lcl, ucl };
  });
}

/** A statistic as it is shown and stored: two decimals, "—" when there is none. */
export const fmt2 = (v: number | null | undefined): string => (v == null ? "—" : v.toFixed(2));

/** Below this a category's limits rest on too few counts to trust unreviewed. */
export const FEW_COUNTS = 20;

/** Is there anything to upload? Three empty categories would only wipe the
 *  active set. */
export const hasAnyLimit = (rows: readonly CalculatedLimit[]): boolean => rows.some((r) => r.n > 0);

/** The calculated set → the "Upload as active" body rows. Numbers travel as the
 *  text the user reviewed. */
export function toLimitUploadRows(
  rows: readonly CalculatedLimit[], remarks: Readonly<Record<number, string>>,
): Record<string, unknown>[] {
  const txt = (v: number | null) => (v == null ? null : v.toFixed(2));
  return rows.map((r) => ({
    category: String(r.category),
    noOfCounts: r.n,
    mean: txt(r.mean),
    sd: txt(r.sd),
    iqr: txt(r.iqr),
    lcl: txt(r.lcl),
    ucl: txt(r.ucl),
    remark: nul(remarks[r.category] ?? ""),
  }));
}

/** A stored (active) control limit, as GET /limits returns it. */
export interface PlmActiveLimit {
  category: string;
  lcl: string;
  ucl: string;
  noOfCounts: string;
  basis: string;
  remark: string;
  createdAt: string;
}

export function toActiveLimit(r: Record<string, unknown>): PlmActiveLimit {
  const s = (k: string) => (r[k] == null ? "" : String(r[k]));
  return {
    category: s("category"), lcl: s("lcl"), ucl: s("ucl"), noOfCounts: s("noOfCounts"),
    basis: s("basis"), remark: s("remark"), createdAt: s("createdAt"),
  };
}

function nul(v: string): string | null {
  const s = v.trim();
  return s === "" ? null : s;
}
